<script setup lang="ts">
import { computed } from 'vue'
import { formatting, useContext, useDialogs, useLocalization } from '@hantera/portal-app'
import type { JobDefinition, JobDefinitionParameter, JobStatus } from '@/backend/jobs.types'
import ScheduleJobDialog from './ScheduleJobDialog.vue'

const props = defineProps<{
  job: JobStatus
  jobDefinition: JobDefinition
  parameterMetadata?: Record<string, JobDefinitionParameter> | null
  onReschedule?: (parameters: Record<string, unknown>) => Promise<void>
  onClose?: () => void
  parentContext: ReturnType<typeof useContext>
}>()

const t = useLocalization('jobs')
const dialogs = useDialogs()

/**
 * A job that never ran, or was created outside any invocation context, has no trace — so no link is
 * offered rather than one leading to an empty view.
 */
const canViewTrace = computed(
  () => !!props.job.traceId && props.parentContext.authorize('traces:read'),
)

/**
 * Opens the trace with this job's own node selected.
 *
 * The span is addressed as `{kind}:{spanId}` — the same form the trace graph emits when a node is
 * clicked. Selecting it matters most for a job scheduled by another job, which sits as a descendant
 * somewhere in the tree rather than at its root: without it the operator arrives at a graph of
 * near-identical leaves and has to find their own job in it.
 */
function viewTrace() {
  props.parentContext.openView('/system/monitoring', {
    query: {
      tab: 'traces',
      traceId: props.job.traceId!,
      span: `job:${props.job.jobId}`,
    },
  })
  props.onClose?.()
}

const hasParameters = computed(() => {
  const params = props.job.parameters
  return params && Object.keys(params).length > 0
})

const parameterEntries = computed(() => {
  if (!props.job.parameters) return []
  return Object.entries(props.job.parameters)
})

function formatParamValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return '—'

  const meta = props.parameterMetadata?.[key]
  if (!meta) return String(value)

  switch (meta.type) {
    case 'instant':
      return formatting.dateTime(value as any)
    case 'date':
      return formatting.date(value as any)
    case 'number':
      return typeof value === 'number' ? value.toLocaleString() : String(value)
    case 'boolean':
    case 'bool':
      return value === true ? 'true' : value === false ? 'false' : String(value)
    default:
      return String(value)
  }
}

const statusColor = computed(() => {
  switch (props.job.state) {
    case 'completed':
      return 'hsl(142, 71%, 45%)'
    case 'failed':
      return 'hsl(0, 84%, 60%)'
    case 'running':
      return 'hsl(200, 71%, 45%)'
    case 'pending':
      return 'hsl(45, 100%, 51%)'
    case 'cancelled':
      return 'hsl(280, 71%, 45%)'
    default:
      return 'hsl(var(--color-50))'
  }
})

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleString()
}

function handleReschedule() {
  const controller = dialogs.openModal(
    t('detail.dialog.reschedule'),
    ScheduleJobDialog,
    false,
    {
      jobDefinitionId: props.jobDefinition.jobDefinitionId,
      defaultValues: props.job.parameters ?? {},
      onSchedule: async (parameters: Record<string, unknown>) => {
        await props.onReschedule?.(parameters)
        controller.close()
        props.onClose?.()
      },
      onCancel: () => controller.close(),
    },
  )
}
</script>

<template>
  <div class="job-dialog-content p2">
    <section class="status-section">
      <div class="status-row">
        <span class="label">{{ t('detail.dialog.status') }}</span>
        <span class="status-badge" :style="{ backgroundColor: statusColor }">
          {{ job.state }}
        </span>
      </div>
      <div class="info-row">
        <span class="label">{{ t('detail.dialog.scheduledAt') }}</span>
        <span class="value">{{ formatDate(job.scheduledAt) }}</span>
      </div>
      <div class="info-row" v-if="job.startedAt">
        <span class="label">{{ t('detail.dialog.startedAt') }}</span>
        <span class="value">{{ formatDate(job.startedAt) }}</span>
      </div>
      <div class="info-row" v-if="job.finishedAt">
        <span class="label">{{ t('detail.dialog.finishedAt') }}</span>
        <span class="value">{{ formatDate(job.finishedAt) }}</span>
      </div>
    </section>

    <section class="parameters-section" v-if="hasParameters">
      <h3>{{ t('detail.dialog.parameters') }}</h3>
      <div class="parameters-table">
        <div v-for="[key, value] in parameterEntries" :key="key" class="param-row">
          <span class="param-name">{{ key }}</span>
          <span class="param-value">{{ formatParamValue(key, value) }}</span>
        </div>
      </div>
    </section>

    <section class="result-section" v-if="job.result !== null && job.result !== undefined">
      <h3>{{ t('detail.dialog.result') }}</h3>
      <pre class="result-json">{{ JSON.stringify(job.result, null, 2) }}</pre>
    </section>

    <div class="dialog-actions">
      <button v-if="canViewTrace" class="btn-secondary" @click="viewTrace">
        {{ t('detail.dialog.viewTrace') }}
      </button>
      <button v-if="onReschedule" class="btn-primary" @click="handleReschedule">
        {{ t('detail.dialog.reschedule') }}
      </button>
      <button class="btn-secondary" @click="onClose?.()">
        {{ t('detail.dialog.close') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.job-dialog-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 400px;
}

section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: hsl(var(--color-30));
}

.status-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.label {
  font-size: 13px;
  color: hsl(var(--color-40));
}

.value {
  font-size: 13px;
  color: hsl(var(--color-20));
}

.status-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: white;
}

.result-json {
  background: hsl(var(--color-95));
  border: 1px solid hsl(var(--color-80));
  border-radius: 4px;
  padding: 0.75rem;
  font-size: 12px;
  overflow-x: auto;
  max-height: 200px;
  overflow-y: auto;
  margin: 0;
}

.parameters-table {
  display: flex;
  flex-direction: column;
  background: hsl(var(--color-97));
  border: 1px solid hsl(var(--color-88));
  border-radius: 4px;
  overflow: hidden;
}

.param-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 0.75rem;
  border-bottom: 1px solid hsl(var(--color-92));
  font-size: 13px;
}

.param-row:last-child {
  border-bottom: none;
}

.param-name {
  font-family: monospace;
  font-size: 12px;
  color: hsl(var(--color-40));
}

.param-value {
  color: hsl(var(--color-20));
}

.dialog-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid hsl(var(--color-90));
}
</style>
